/**
 * variantHelpers.ts — Utilidades puras para trabajar con variantes de producto.
 *
 * QUÉ HACE:
 *   Funciones sin React/estado para manipular combinaciones de variantes.
 *   Se usan tanto en el hook `useVariantSelection` como en componentes que
 *   necesitan construir una línea de carrito SIN montar el hook (quick-add
 *   desde una tarjeta del catálogo, importaciones masivas, etc.).
 *
 * QUÉ EXPORTA:
 *   - buildVariantKey(seleccion)         → string estable "k=v|k=v"
 *   - sameCombo(a, b)                    → igualdad estructural
 *   - findSkuByKey(producto, variantKey) → VariantSKU | null
 *   - pickDefaultSelection(producto)     → Record<groupId, optionId>
 *       Elige la primera opción disponible con stock > 0 para cada grupo
 *       REQUERIDO. Si el producto no tiene variantes, retorna {}.
 *   - pickDefaultVariant(producto)       → { variantKey, skuActivo, labels, ... }
 *       Envoltorio listo para el quick-add: selección + SKU + stock + labels.
 *
 * DEPENDE DE: types (Producto, VariantGroup, VariantSKU)
 *
 * QUÉ NO HACE:
 *   - No toca estado, carrito ni backend.
 *   - No muta el producto.
 *
 * REGLA: si cambias la forma de construir `variantKey`, hazlo aquí en un solo
 * lugar. Los handlers mock y los componentes de UI consumen esta misma función.
 */
import type {
  CartItem,
  Producto,
  VariantGroup,
  VariantOption,
  VariantSKU,
} from "@/types";

export interface VariantSelectedLabel {
  groupId: string;
  groupLabel: string;
  optionId: string;
  optionLabel: string;
  precioExtra?: number;
}

export interface VariantStockLineRef {
  id?: string;
  productoId?: string;
  quantity?: number;
  cantidad?: number;
  variantKey?: string;
}

export type VariantStockValidationResult =
  | { ok: true }
  | { ok: false; message: string };

/** Genera clave estable a partir de la combinación (ordenada por groupId). */
export const buildVariantKey = (seleccion: Record<string, string>): string => {
  const keys = Object.keys(seleccion).sort();
  if (keys.length === 0) return "";
  return keys.map((k) => `${k}=${seleccion[k]}`).join("|");
};

/** Cantidad de combinaciones posibles (producto cartesiano de opciones por grupo). */
export const combinacionesTeoricasCount = (
  variantes: VariantGroup[],
): number => {
  const con = variantes.filter((g) => g.opciones.length > 0);
  if (con.length === 0) return 0;
  return con.reduce((acc, g) => acc * g.opciones.length, 1);
};

/**
 * Si `variantesSKUs` tiene una fila por cada combinación teórica (matriz llena),
 * reduce a 3 muestras (primera, central, última por orden de clave) para el flujo
 * disperso del inventario. No altera listas parciales ni manuales.
 */
/**
 * Opciones que el cliente puede elegir en un grupo según SKUs ya definidos
 * y lo ya seleccionado en grupos anteriores (evita mezclas inexistentes).
 */
export const opcionesVisiblesParaGrupo = (
  indiceGrupo: number,
  grupos: VariantGroup[],
  skus: VariantSKU[],
  seleccion: Record<string, string>,
): VariantOption[] => {
  const g = grupos[indiceGrupo];
  if (!g) return [];
  if (skus.length === 0) return g.opciones;

  const relevantes = skus.filter((sku) => {
    for (let i = 0; i < indiceGrupo; i++) {
      const gid = grupos[i].id;
      const sel = seleccion[gid];
      if (!sel || sku.combinacion[gid] !== sel) return false;
    }
    return true;
  });

  const permitidos = new Set<string>();
  for (const s of relevantes) {
    const oid = s.combinacion[g.id];
    if (oid) permitidos.add(oid);
  }
  return g.opciones.filter((o) => permitidos.has(o.id));
};

/** Stock total para mostrar en caja/catálogo cuando hay SKUs dispersos. */
export const stockAgregadoDesdeSkus = (producto: Producto): number => {
  const skus = producto.variantesSKUs ?? [];
  if (skus.length === 0) return Number(producto.stock ?? 0);
  return skus.reduce((a, s) => a + (s.stock ?? 0), 0);
};

/** Alias semántico: unidades vendibles del producto (POS, inventario, grilla). */
export const stockVendibleProducto = (producto: Producto): number =>
  stockAgregadoDesdeSkus(producto);

/**
 * Stock efectivo de una línea de carrito.
 * Prioridad: stockVariante → SKU por variantKey → agregado SKU → stock plano.
 */
export const stockEfectivoLineaCarrito = (
  item: Pick<CartItem, "stock" | "stockVariante" | "variantKey" | "id">,
  producto?: Producto | null,
): number => {
  if (item.stockVariante != null) return item.stockVariante;
  if (producto && item.variantKey) {
    const sku = findSkuByKey(producto, item.variantKey);
    if (sku) return sku.stock ?? 0;
  }
  if (producto?.variantesSKUs?.length) {
    return stockAgregadoDesdeSkus(producto);
  }
  return item.stock ?? producto?.stock ?? 9999;
};

/**
 * Intenta leer variantKey desde el nombre de línea demo
 * (ej. "Gafas de Sol — Color: Negro" → grp_color=op_neg).
 */
export const parseVariantKeyFromLineNombre = (
  producto: Pick<Producto, "variantes">,
  nombre: string,
): string | undefined => {
  const specPart = nombre.split("—").slice(1).join("—").trim();
  if (!specPart) return undefined;

  const grupos = producto.variantes ?? [];
  const seleccion: Record<string, string> = {};

  for (const segment of specPart.split(/[,·]/)) {
    const match = segment.trim().match(/^(.+?):\s*(.+)$/);
    if (!match) continue;
    const [, groupLabel, optionLabel] = match;
    const grupo = grupos.find(
      (g) => g.label.toLowerCase() === groupLabel.trim().toLowerCase(),
    );
    const opcion = grupo?.opciones.find(
      (o) => o.label.toLowerCase() === optionLabel.trim().toLowerCase(),
    );
    if (grupo && opcion) seleccion[grupo.id] = opcion.id;
  }

  if (Object.keys(seleccion).length === 0) return undefined;
  return buildVariantKey(seleccion);
};

type StockMutableProduct = Pick<Producto, "stock" | "variantesSKUs"> &
  Record<string, unknown>;

/**
 * Aplica delta de stock (+ devolución / − venta) respetando SKUs por variantKey.
 * Sincroniza `producto.stock` con la suma de SKUs cuando hay variantes.
 */
export const applyStockDeltaToProduct = (
  producto: StockMutableProduct | Producto,
  delta: number,
  variantKey?: string,
): StockMutableProduct => {
  const skus = producto.variantesSKUs ?? [];
  if (skus.length === 0) {
    const current = Number(producto.stock ?? 0);
    return { ...producto, stock: Math.max(0, current + delta) };
  }

  let remaining = Math.abs(delta);
  const sign = delta >= 0 ? 1 : -1;

  const nextSKUs = skus.map((sku) => ({
    ...sku,
    combinacion: { ...sku.combinacion },
  }));

  const touchSku = (idx: number, amount: number) => {
    const sku = nextSKUs[idx];
    const current = sku.stock ?? 0;
    nextSKUs[idx] = {
      ...sku,
      stock: sign > 0 ? current + amount : Math.max(0, current - amount),
    };
    remaining -= amount;
  };

  if (variantKey) {
    const idx = nextSKUs.findIndex(
      (sku) => buildVariantKey(sku.combinacion) === variantKey,
    );
    if (idx >= 0 && remaining > 0) {
      const take = Math.min(
        remaining,
        sign < 0 ? (nextSKUs[idx].stock ?? 0) : remaining,
      );
      touchSku(idx, take);
    }
  }

  if (remaining > 0) {
    const order =
      sign < 0
        ? [...nextSKUs.keys()].sort(
            (a, b) => (nextSKUs[b].stock ?? 0) - (nextSKUs[a].stock ?? 0),
          )
        : [...nextSKUs.keys()];
    for (const idx of order) {
      if (remaining <= 0) break;
      if (sign < 0) {
        const avail = nextSKUs[idx].stock ?? 0;
        if (avail <= 0) continue;
        touchSku(idx, Math.min(remaining, avail));
      } else {
        touchSku(idx, remaining);
      }
    }
  }

  const aggregated = nextSKUs.reduce((acc, sku) => acc + (sku.stock ?? 0), 0);
  return {
    ...producto,
    stock: aggregated,
    variantesSKUs: nextSKUs,
  };
};

export const trimFullMatrixSkusToSample = (
  variantes: VariantGroup[],
  skus: VariantSKU[],
  muestra = 3,
): VariantSKU[] => {
  const teorico = combinacionesTeoricasCount(variantes);
  if (teorico <= muestra || skus.length !== teorico) return skus;

  const sorted = [...skus].sort((a, b) =>
    buildVariantKey(a.combinacion).localeCompare(
      buildVariantKey(b.combinacion),
    ),
  );
  if (sorted.length <= muestra) return sorted;
  return [
    sorted[0],
    sorted[Math.floor(sorted.length / 2)],
    sorted[sorted.length - 1],
  ];
};

/** Compara dos combinaciones (igualdad total de claves/valores). */
export const sameCombo = (
  a: Record<string, string>,
  b: Record<string, string>,
): boolean => {
  const ak = Object.keys(a);
  const bk = Object.keys(b);
  if (ak.length !== bk.length) return false;
  return ak.every((k) => a[k] === b[k]);
};

/** Busca el SKU que corresponda a una `variantKey` dentro del producto. */
export const findSkuByKey = (
  producto: Producto,
  variantKey: string,
): VariantSKU | null => {
  const skus = producto.variantesSKUs ?? [];
  if (!variantKey || skus.length === 0) return null;
  return (
    skus.find((s) => buildVariantKey(s.combinacion) === variantKey) ?? null
  );
};

/**
 * Valida el stock total solicitado por combinación, incluso cuando el mismo
 * SKU está repartido en varias líneas con personalizaciones diferentes.
 */
export const validateVariantStockForSale = (
  products: Producto[],
  lines: VariantStockLineRef[],
): VariantStockValidationResult => {
  for (const product of products) {
    const skus = product.variantesSKUs ?? [];
    if (skus.length === 0 || product.ventaPorPeso) continue;

    const productLines = lines.filter(
      (line) => (line.id ?? line.productoId) === product.id,
    );
    if (productLines.length === 0) continue;

    const requestedByVariant = new Map<string, number>();
    for (const line of productLines) {
      const variantKey = line.variantKey?.trim();
      if (!variantKey) {
        return {
          ok: false,
          message: `Selecciona una combinación válida para ${product.nombre}`,
        };
      }
      const quantity = Math.max(0, line.quantity ?? line.cantidad ?? 0);
      requestedByVariant.set(
        variantKey,
        (requestedByVariant.get(variantKey) ?? 0) + quantity,
      );
    }

    for (const [variantKey, requested] of requestedByVariant) {
      const sku = findSkuByKey(product, variantKey);
      if (!sku) {
        return {
          ok: false,
          message: `La combinación seleccionada de ${product.nombre} ya no existe`,
        };
      }
      if ((sku.stock ?? 0) < requested) {
        return {
          ok: false,
          message: `Stock insuficiente para la combinación de ${product.nombre}. Disponible: ${sku.stock ?? 0}`,
        };
      }
    }
  }

  return { ok: true };
};

/**
 * Devuelve la selección por defecto para un producto:
 *   - Primer grupo → primera opción con stock disponible (si hay SKUs).
 *   - Si no hay SKUs, simplemente la primera opción de cada grupo REQUIRED.
 *   - Si el producto no tiene variantes o no tiene grupos requeridos, {}.
 */
export const pickDefaultSelection = (
  producto: Producto,
): Record<string, string> => {
  const grupos: VariantGroup[] = producto.variantes ?? [];
  const requiredGrupos = grupos.filter((g) => g.required);
  if (requiredGrupos.length === 0) return {};

  const skus = producto.variantesSKUs ?? [];

  // Lista dispersa: sólo claves que vienen del SKU (sin rellenar con otras opciones).
  if (skus.length > 0) {
    const sorted = [...skus].sort((a, b) =>
      buildVariantKey(a.combinacion).localeCompare(
        buildVariantKey(b.combinacion),
      ),
    );
    const conStock = sorted.find((s) => (s.stock ?? 0) > 0);
    const elegido = conStock ?? sorted[0];
    if (elegido?.combinacion) {
      const out: Record<string, string> = {};
      for (const g of grupos) {
        const v = elegido.combinacion[g.id];
        if (v) out[g.id] = v;
      }
      return out;
    }
  }

  // Fallback: primera opción de cada grupo required
  const out: Record<string, string> = {};
  for (const g of requiredGrupos) {
    if (g.opciones[0]) out[g.id] = g.opciones[0].id;
  }
  return out;
};

export interface DefaultVariantResult {
  seleccion: Record<string, string>;
  variantKey: string;
  skuActivo: VariantSKU | null;
  stockDisponible: number;
  precioFinal: number;
  imagenActiva: string | null;
  labelsSeleccionados: VariantSelectedLabel[];
}

/** Opciones de carrito derivadas de un SKU concreto (escaneo de variante en POS). */
export interface VariantCartAddOptions {
  variantKey: string;
  variantesSeleccionadas: VariantSelectedLabel[];
  stockDisponible: number;
  skuVariante?: string;
  precioFinal: number;
}

/**
 * Construye payload de `addToCart` a partir de una fila variantesSKUs.
 * Usado cuando la pistola escanea el código de barras de la combinación.
 */
export const buildCartOptionsFromSku = (
  producto: Producto,
  sku: VariantSKU,
): VariantCartAddOptions => {
  const grupos: VariantGroup[] = producto.variantes ?? [];
  const seleccion = sku.combinacion;
  const variantKey = buildVariantKey(seleccion);

  let precioExtra = 0;
  const labels: VariantSelectedLabel[] = [];

  for (const g of grupos) {
    const optId = seleccion[g.id];
    const opt = optId ? g.opciones.find((o) => o.id === optId) : undefined;
    if (!opt) continue;
    precioExtra += opt.precioExtra ?? 0;
    labels.push({
      groupId: g.id,
      groupLabel: g.label,
      optionId: opt.id,
      optionLabel: opt.label,
      ...(opt.precioExtra != null && opt.precioExtra > 0
        ? { precioExtra: opt.precioExtra }
        : {}),
    });
  }

  const precioFinal =
    sku.precioOverride != null
      ? sku.precioOverride
      : producto.precio + precioExtra;

  return {
    variantKey,
    variantesSeleccionadas: labels,
    stockDisponible: sku.stock ?? 0,
    ...(sku.sku ? { skuVariante: sku.sku } : {}),
    precioFinal,
  };
};

/**
 * Devuelve todos los datos derivados de la selección por defecto — útil para
 * hacer quick-add al carrito sin montar el hook.
 * Si el producto no tiene variantes, devuelve valores neutros.
 */
export const pickDefaultVariant = (
  producto: Producto,
): DefaultVariantResult => {
  const grupos: VariantGroup[] = producto.variantes ?? [];
  const seleccion = pickDefaultSelection(producto);
  const variantKey = buildVariantKey(seleccion);
  const skuActivo = findSkuByKey(producto, variantKey);

  let precioExtra = 0;
  const labels: VariantSelectedLabel[] = [];
  let imagen: string | null = null;
  const opcionesSel: Record<string, VariantOption | undefined> = {};

  for (const g of grupos) {
    const optId = seleccion[g.id];
    const opt = optId ? g.opciones.find((o) => o.id === optId) : undefined;
    opcionesSel[g.id] = opt;
    if (opt) {
      precioExtra += opt.precioExtra ?? 0;
      labels.push({
        groupId: g.id,
        groupLabel: g.label,
        optionId: opt.id,
        optionLabel: opt.label,
        ...(opt.precioExtra != null && opt.precioExtra > 0
          ? { precioExtra: opt.precioExtra }
          : {}),
      });
      if (g.kind === "color" && opt.imagen && !imagen) {
        imagen = opt.imagen;
      }
    }
  }

  const precioFinal =
    skuActivo?.precioOverride != null
      ? skuActivo.precioOverride
      : producto.precio + precioExtra;

  const stockDisponible = skuActivo ? skuActivo.stock : producto.stock;

  return {
    seleccion,
    variantKey,
    skuActivo,
    stockDisponible,
    precioFinal,
    imagenActiva: imagen,
    labelsSeleccionados: labels,
  };
};
